import { HelpLangRequest } from "../../../loader/lang/HelpLangRequest";
import { LangRequestJSON } from "../../../loader/lang/LangRequestJSON";
import { Dictionary } from "../../../util/collection/Dictionary";
import { Logger } from "../../../util/log/Logger";
import { WebUtil } from "../../../util/web/WebUtil";
import { AppData } from "../../AppData";
import { LangConstants } from "../../Constants";
import { CommonGameEvent } from "../../controller/event/CommonGameEvent";
import { FactoryManager } from "../../factory/FactoryManager";
import { LanguageManager, Locale } from "../../manager/LanguageManager";
import { Renderer } from "../../renderer/Renderer";
import { CommonLang } from "../lang/CommonLang";
import { CommonHTMLHelpStyle } from "../style/CommonHTMLHelpStyle";
import { HelpSearch } from "./HelpSearch";
/**
 * AbstractHTMLHelp.ts
 */
export class AbstractHTMLHelp {
    constructor(properties) {
        this.maxPercentScrolled = 0;
        this.uiProperties = properties;
        this.createModel();
        this.loadLang();
        this.addMainDiv();
    }
    createModel() {
        this.factory = FactoryManager.getInstance().getFactory();
        this.model = this.factory.getModel();
        this.wrapperConfig = this.factory.getWrapperConfig();
        this.operatorConfig = this.wrapperConfig.getOperatorConfig();
        this.operatorParams = this.wrapperConfig.getOperatorParameters();
    }
    addMainDiv() {
        this.mainDiv = document.createElement("div");
        this.mainDiv.id = CommonHTMLHelpStyle.MAIN_DIV_ID;
        let divStyle = this.mainDiv.style;
        const metaBar = document.getElementById("metaBar");
        divStyle.top = metaBar ? metaBar.style.height : "0px";
        document.body.appendChild(this.mainDiv);
    }
    /**
     * Show help dialog - called by DialogController
     */
    open() {
        if (!this.params) {
            this.createParams();
            this.setRtpValues();
        }
        this.updateValues();
        this.handlePending();
        this.handlePlayControls();
        this.handleExtraSpin();
        this.handleMaxWin();
        this.handleAvailableWin();
        this.handleHelpOpen();
        this.model.dispatchKPI("TITAN_STATE", "HELP_PAGE_OPEN", null);
    }
    /**
     * Hide help dialog - called by DialogController
     */
    close() {
        this.model.dispatchKPI("TITAN_STATE", "HELP_PAGE_SCROLL_PERCENT", this.maxPercentScrolled.toString());
        this.model.dispatchKPI("TITAN_STATE", "HELP_PAGE_CLOSE", null);
        this.mainDiv.style.display = "none";
        this.model.dispatch(CommonGameEvent.HELP_DIALOG_CLOSE);
        Renderer.getInstance().getStage().interactiveChildren = true;
    }
    ///STUB FUNCTIONS: overwrite in child
    /**
     * Creates the params for all help strings
     */
    createParams() {
        this.params = new Dictionary();
    }
    /**
     * Inserts images to the loaded page
     * Populate with calls to addRow, addColum, addImageGrid
     */
    addImages() {
        // examples:
        // Single centred image, no header or footer
        // this.addColumn("help.slingo.standard.main.main", [this.createImage("logo.webp")], "15em");
        //
        // Image row with headers and footers, spanning the full width of the page
        // this.addRow(
        //     "help.spaceinvaders.bonus.symbolsAliens.title",
        //     [
        //         this.createImage("Game-PayTable-CollectAlien0.webp", "help.spaceinvaders.bonus.symbolsAliensTable.line4", "help.spaceinvaders.bonus.symbolsAliensTable.line7"),
        //         this.createImage("Game-PayTable-CollectAlien1.webp", "help.spaceinvaders.bonus.symbolsAliensTable.line5", "help.spaceinvaders.bonus.symbolsAliensTable.line8"),
        //         this.createImage("Game-PayTable-CollectAlien2.webp", "help.spaceinvaders.bonus.symbolsAliensTable.line6", "help.spaceinvaders.bonus.symbolsAliensTable.line9")
        //     ],
        //     "calc(min(10em, 30%))"
        // );
        //
        // Row of images closely packed together in the centre with no headers or footers
        // this.addCompactRow("help.moneytrain.moneytrain.bronzewheel.title", [
        //     this.createImage("Game-Wheel-03.webp"),
        //     this.createImage("Game-Wheel-02.webp"),
        //     this.createImage("Game-Wheel-01.webp")
        // ], "33em", "1em");
    }
    /**
     * inserts any custom HTML into the standard web page
     * populate with calls to addCustomHTMLSnippet
     */
    addCustomHTML() {
        //usage:
        //this.addCustomHTMLSnippet(<tag to drop HTML under>, `<htmlSnippet>`);
    }
    ///END STUB FUNCTIONS
    /**
     * Tracks page scrolling and records distance scrolled
     */
    startScrollTracking() {
        const scrollDiv = document.getElementById("htmlHelpScroll");
        if (scrollDiv) {
            scrollDiv.addEventListener("scroll", () => {
                const scrollRatio = scrollDiv.scrollTop /
                    (scrollDiv.scrollHeight - scrollDiv.clientHeight) || 0;
                const scrollPercent = Math.round(scrollRatio * 100);
                if (scrollPercent > this.maxPercentScrolled) {
                    this.maxPercentScrolled = scrollPercent;
                }
            });
        }
    }
    /**
     * Sets the RTP parameter values in the help page
     */
    setRtpValues() {
        const rtp = this.factory.getWrapperConfig().getGame().getRtp();
        const baseRtp = rtp?.base
            ? (parseFloat(rtp.base) * 100).toFixed(2) + "%"
            : "MISSING";
        const extraSpinsRtp = rtp?.extra_spins
            ? (parseFloat(rtp.extra_spins) * 100).toFixed(2) + "%"
            : "MISSING";
        this.params.setValue("{param.rtp.standard}", baseRtp);
        this.params.setValue("{param.rtp.extra}", extraSpinsRtp);
        this.params.setValue("{Base.RTP}", baseRtp);
        this.params.setValue("{ExtraSpin.RTP}", extraSpinsRtp);
    }
    /**
     * Generates HTML from the help json string key names.
     * Key suffixes define element created
     * Each element uses the key as its id, and contains the key value text as its contents
     * By default,
     *  - keys ending in "main" === h3
     *  - keys ending in "title" === h4
     *  - keys ending in "line" === p
     *  - key suffixes are defined in CommonHTMLHelpStyle.ts, but this can be overwritten in properties.
     */
    addElements() {
        const content = document.querySelector("#content");
        for (let key of this.language.getKeys()) {
            let element = null;
            if (key.endsWith(this.uiProperties.getKeyStyle().SUFFIX_KEY_H3)) {
                element = document.createElement("h3");
            }
            else if (key.endsWith(this.uiProperties.getKeyStyle().SUFFIX_KEY_H4)) {
                element = document.createElement("h4");
            }
            else if (key.includes(this.uiProperties.getKeyStyle().SUFFIX_KEY_P)) {
                element = document.createElement("p");
            }
            if (element) {
                element.id = key;
                content.appendChild(element);
            }
        }
    }
    /**
     * Searches the help page for an element with the given id.
     * If found - removes it from the DOM and returns it
     * If not found - creats a paragraph with the element as the id and returns that
     * @param id - id to search for
     */
    extractElement(id) {
        let element = id ? document.getElementById(id) : null;
        if (element) {
            element.parentElement.removeChild(element);
        }
        else {
            element = this.createParagraph(id);
        }
        return element;
    }
    /**
     * Creates a <p> element with the given id
     * @returns
     */
    createParagraph(id) {
        const element = document.createElement("p");
        element.id = id;
        return element;
    }
    /**
     * Traverses the document, taking the lang keys from each element id and setting their content to the lang value.
     */
    addLanguage() {
        for (let key of this.language.getKeys()) {
            const element = document.getElementById(key);
            const langText = this.language.getValue(key);
            if (element) {
                if (langText === " ") {
                    element.style.display = "none";
                }
                else {
                    element.style.display = "";
                    element.innerText = langText;
                }
            }
        }
    }
    /**
     * Helper function to create a centred column containing images
     * @param id - title element id to append the image column to
     * @param images - images to add to the column
     * @param maxWidth - max-width css style to apply to each image
     * @param maxHeight - max-height css style to apply to each image
     */
    addColumn(id, images, maxWidth, maxHeight) {
        this.addImageGrid({
            id: id,
            images: images,
            maxWidth: maxWidth,
            maxHeight: maxHeight,
            columns: 1,
        });
    }
    /**
     * Helper function to create a single row of images
     * @param id - title element id to append the image row to
     * @param images -  images to add to the column
     * @param maxWidth - max-width css style to apply to each image
     * @param maxHeight - max-height css style to apply to each image
     */
    addRow(id, images, maxWidth, maxHeight) {
        this.addImageGrid({
            id: id,
            images: images,
            maxWidth: maxWidth,
            maxHeight: maxHeight,
            rows: 1,
        });
    }
    /**
     * Helper function to create a single row of images, centred, with minimal spacing between row elements
     * @param id - title element id to append the image row to
     * @param images - images to add to the column
     * @param rowWidth - width of all elements in row
     * @param paddingX - gap between grid elements
     */
    addCompactRow(id, images, rowWidth, paddingX) {
        this.addImageGrid({
            id: id,
            images: images,
            rows: 1,
            rowWidth: rowWidth,
            paddingX: paddingX,
        });
    }
    /**
     * Appends a set of images to the help page arranged in a grid, with optional headers and footers for each image
     * @param config - config object for styling params
     * @param {string} config.id - title element id to append the image row to
     * @param {IHelpImage} config.images - images to add to the column
     * @param {string} config.images.src - relative path to image
     * @param {string} [config.images.header] - text above image
     * @param {string} [config.images.footer] - text below image
     * @param {string} [config.maxWidth] - max image width
     * @param {string} [config.maxHeight] - max image height
     * @param {number} [config.rows] - number of rows per grid column
     * @param {number} [config.columns] - number of columns per grid row
     * @param {string} [config.paddingY] - padding between rows
     * @param {string} [config.paddingX] - padding between columns
     * @param {string} [config.padding] - padding between rows and columns
     * @param {string} [config.gridTemplateColumns] - custom gridTemplateColumns style property
     * @param {string} [config.gridTemplateRows] - custom gridTemplateRows style property
     * @param {string} [config.rowWidth] - max width of entire row. Supercedes config.maxWidth
     */
    addImageGrid(config) {
        const paddingX = config.paddingX || config.padding || "0em";
        const paddingY = config.paddingY || config.padding || "0em";
        const path = AppData.VERSION + "/help/help-assets/";
        const totalColumns = config.columns
            ? config.columns
            : config.rows
                ? Math.ceil(config.images.length / config.rows)
                : -1;
        const totalRows = config.rows
            ? config.rows
            : config.columns
                ? Math.ceil(config.images.length / config.columns)
                : -1;
        const columnsProperty = totalColumns > 0 ? `repeat(${totalColumns}, 1fr)` : "none";
        const rowsProperty = totalRows > 0 ? `repeat(${totalRows}, 1fr)` : "none";
        const gridContainer = document.createElement("div");
        gridContainer.classList.add("image-grid");
        gridContainer.style.gridTemplateColumns = config.gridTemplateColumns
            ? config.gridTemplateColumns
            : columnsProperty;
        gridContainer.style.gridTemplateRows = config.gridTemplateRows
            ? config.gridTemplateColumns
            : rowsProperty;
        gridContainer.style.columnGap = `${paddingX || "normal"}`;
        gridContainer.style.rowGap = `${paddingY || "normal"}`;
        if (config.rowWidth) {
            gridContainer.style.width = config.rowWidth;
            gridContainer.style.margin = "0 auto";
        }
        //break images into rows
        let imageRows = [];
        for (let ss = 0; ss < config.images.length; ss += totalColumns) {
            imageRows.push(config.images.slice(ss, ss + totalColumns));
        }
        const noHeaders = imageRows.every((imageRow) => imageRow.every((image) => image.header === null));
        const noFooters = imageRows.every((imageRow) => imageRow.every((image) => image.footer === null));
        //put headers, images and footers on a grid row each
        imageRows.forEach((imageRow) => {
            if (!noHeaders) {
                imageRow.forEach((image) => {
                    const header = this.extractElement(image.header);
                    gridContainer.appendChild(header);
                });
            }
            imageRow.forEach((image) => {
                const img = document.createElement("img");
                img.src = `${path}${image.src}`;
                gridContainer.appendChild(img);
                if (!config.rowWidth) {
                    this.applyMax(img, config.maxWidth, config.maxHeight);
                }
            });
            if (!noFooters) {
                imageRow.forEach((image) => {
                    const footer = this.extractElement(image.footer);
                    gridContainer.appendChild(footer);
                });
            }
        });
        this.appendAfter(config.id, gridContainer);
    }
    /**
     * Inserts any optional HTML to the page. Any duplicate ids present in content will be extracted from the page
     * @param id - title element id to append the custom HTML to
     * @param content - custom HTML to insert
     */
    addCustomHTMLSnippet(id, content) {
        const contentHTML = document.createElement("div");
        contentHTML.innerHTML = content;
        const ids = [...contentHTML.querySelectorAll("*[id]")];
        ids.forEach((elem) => {
            this.extractElement(elem.id);
        });
        const predecessor = document.getElementById(id);
        if (predecessor) {
            predecessor.parentNode.insertBefore(contentHTML, predecessor.nextSibling);
        }
    }
    applyMax(element, maxWidth, maxHeight) {
        if (maxWidth) {
            element.style.maxWidth = maxWidth;
        }
        if (maxHeight) {
            element.style.maxHeight = maxHeight;
        }
    }
    handlePending() {
        const autoCompleteInterval = this.operatorConfig.getAutoCompleteInterval() || 0;
        if (!this.operatorConfig.isAutoCompleteEnabled() ||
            autoCompleteInterval <= 0) {
            this.filterElementsById(this.uiProperties.pendingGameElements);
        }
    }
    handlePlayControls() {
        if (!this.model.getResponsibleGaming().isEnabled()) {
            this.filterElementsById(this.uiProperties.playControlsGameElements);
        }
    }
    handleExtraSpin() {
        if (!this.model.getResponsibleGaming().isEnabled()) {
            this.filterElementsById(this.uiProperties.extraSpinElements);
        }
    }
    handleMaxWin() {
        const operatorParams = this.wrapperConfig.getOperatorParameters();
        const locale = LanguageManager.getInstance().getLocale();
        if (operatorParams.getEnvironmentKey() === "NJ" ||
            (locale !== Locale.AMERICAN &&
                locale !== Locale.ENGLISH &&
                locale !== Locale.ENGLISH_CANADIAN)) {
            this.filterElementsById(this.uiProperties.maxWinElements);
        }
    }
    handleAvailableWin() {
        if (!this.operatorConfig.isYouCouldWinEnabled()) {
            this.filterElementsById(this.uiProperties.availableWinElements);
        }
    }
    filterElementsById(idFilter) {
        const filterIds = Array.isArray(idFilter) ? idFilter : [idFilter];
        filterIds.forEach((id) => {
            const elements = document.querySelectorAll(`[id*="${id}"]`);
            elements.forEach((element) => {
                element.remove();
            });
        });
    }
    handleHelpOpen() {
        Renderer.getInstance().getStage().interactiveChildren = false;
        this.mainDiv.style.display = "block";
    }
    includeHtml() {
        const htmlPath = "/help/help.html";
        const xhttp = new XMLHttpRequest();
        const gameDev = WebUtil.getUrlParameter("gameDev") === "true";
        let path = gameDev ? "version" + htmlPath : AppData.VERSION + htmlPath;
        xhttp.onreadystatechange = () => {
            if (xhttp.readyState === 4) {
                if (xhttp.status === 200) {
                    this.mainDiv.innerHTML = xhttp.responseText;
                    this.addElements();
                    this.addCustomHTML();
                    this.addLanguage();
                    this.addImages();
                    new HelpSearch();
                    const closeButtonCollection = this.mainDiv.getElementsByClassName("close");
                    if (closeButtonCollection && closeButtonCollection.length) {
                        const closeButton = closeButtonCollection[0];
                        closeButton.onclick = () => this.close();
                    }
                    this.startScrollTracking();
                }
                if (xhttp.status === 404) {
                    this.mainDiv.innerHTML = "Page not found.";
                }
            }
        };
        xhttp.open("GET", path, true);
        xhttp.send();
    }
    loadLang() {
        const gameId = this.operatorParams.getGameId();
        const locale = LanguageManager.getInstance().getLocale().getId();
        const req = new HelpLangRequest(gameId, locale);
        req.onComplete(this.handleLangComplete, this);
        req.load();
    }
    handleLangComplete(request) {
        this.language = request.getLang();
        if (this.operatorConfig.hasLangOverrides()) {
            this.loadLangOperator();
        }
        else {
            this.includeHtml();
        }
    }
    loadLangOperator() {
        this.requestOperator = new LangRequestJSON(LangConstants.OPERATOR_URL, this.operatorConfig.getLangOverridePath() +
            "/" +
            this.operatorParams.getGameId());
        this.requestOperator.onComplete(this.handleLangCompleteOperator, this);
        this.requestOperator.load();
    }
    handleLangCompleteOperator() {
        const baseKeys = this.language.getKeys();
        const replaceLang = this.requestOperator.getLang();
        const requestKeys = replaceLang.getKeys();
        const replaceKeys = baseKeys.filter((key) => requestKeys.indexOf(key) > -1);
        replaceKeys.forEach((key) => {
            this.language.setValue(key, replaceLang.getValue(key));
        });
        this.includeHtml();
    }
    updateValues() {
        for (let key of this.language.getKeys()) {
            let value = this.language.getValue(key);
            for (let param of this.params.getKeys()) {
                if (value.indexOf(param) > -1) {
                    value = value.replace(param, this.parseParam(param));
                    const element = document.getElementById(key);
                    Logger.verbose(`HTMLHelp: key: ${key}, value: ${value}`);
                    if (element) {
                        element.innerText = value;
                    }
                    else {
                        Logger.verbose(`HTMLHelp: No element for key: ${key}`);
                    }
                }
            }
        }
        this.updateCloseButton();
        this.updateAccessibleColourToggleButton();
    }
    updateCloseButton() {
        const languageManager = LanguageManager.getInstance();
        const closeButton = document.getElementById("helpCloseBtn");
        if (closeButton) {
            closeButton.innerText = languageManager.getLang(CommonLang.CLOSE_BUTTON);
        }
    }
    updateAccessibleColourToggleButton() {
        const languageManager = LanguageManager.getInstance();
        const accessibleColourButton = document.getElementById("helpAccessibleColourBtn");
        if (accessibleColourButton) {
            accessibleColourButton.innerText = languageManager.getLang(CommonLang.ACCESSIBLE_COLOUR_BUTTON);
            accessibleColourButton.onclick = this.toggleAccessibleColourFunction(true);
        }
    }
    toggleAccessibleColourFunction(withConfirm) {
        return () => {
            if (withConfirm) {
                const confirmText = LanguageManager.getInstance().getLang(CommonLang.ACCESSIBLE_TOGGLE_CONFIRM);
                if (!confirm(confirmText)) {
                    return;
                }
            }
            document.getElementById("htmlHelpScroll")?.classList.toggle("accessibleColour");
            document.getElementById("game-canvas")?.classList.toggle("accessibleColour");
            const accessibleColourButton = document.getElementById("helpAccessibleColourBtn");
            accessibleColourButton.onclick = this.toggleAccessibleColourFunction(!withConfirm);
        };
    }
    parseParam(key) {
        let value = this.params.getValue(key);
        if (typeof value === "function") {
            value = value();
        }
        return value?.toString();
    }
    createImage(src, header = null, footer = null) {
        return {
            src,
            header,
            footer,
        };
    }
    appendAfter(id, newElement) {
        const element = document.getElementById(id);
        if (!element) {
            Logger.error(`HTMLHelp: ELEMENT NOT FOUND: ${id}`);
            return;
        }
        const parent = element.parentNode;
        parent.insertBefore(newElement, element.nextSibling);
    }
}
//# sourceMappingURL=AbstractHTMLHelp.js.map